import { QrCode } from "./../../types/globalTypes";
import { useState, useRef, useEffect } from "react";
import { HiDotsVertical } from "react-icons/hi";
import DeleteQrCodeModal from "./DeleteQrCodeModal";
import QrCodeEditModal from "./QrCodeEditModal";
import QrCodeComponent from "./QrCodeComponent";
import { ArabicOrdinal, EnglishOrdinal } from "@/types/enums";
import { useTranslation } from "react-i18next";

type QrCodeCardProps = Omit<QrCode, "code"> & {
  setNeedRefetch: React.Dispatch<React.SetStateAction<boolean>>;
};

const QrCodeCard: React.FC<QrCodeCardProps> = ({
  _id,
  serial,
  link,
  data,
  setNeedRefetch,
  type,
  branch,
  floor,
  table,
  isAvailableNow
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropDownButtonRef = useRef<HTMLButtonElement>(null);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !dropDownButtonRef.current?.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen(prev => !prev);
  };

  return (
    <tr className="border-b bg-white hover:bg-gray-50">
      {/* qrcode image */}
      <td className="flex items-center gap-2 px-6 py-4 text-sm font-medium text-gray-800">
        <QrCodeComponent data={data} />
      </td>

      {/* serial Number */}
      <td className="px-6 py-4 text-sm text-gray-600">
        <p>{serial}</p>
      </td>

      {/* Link */}
      <td className="px-6 py-4 text-sm text-gray-600">
        <p
          dir="ltr"
          className={`flex items-center ${i18n.language === "ar" ? "justify-end" : "justify-start"}`}
        >
          {type === "table"
            ? `${branch?.nameEN || branch?.nameAR || t("qrCodeCard.unknownBranch")} - ${
                floor?.number
                  ? i18n.language === "ar"
                    ? `الدور ${ArabicOrdinal[Number(floor?.number) - 1]}`
                    : `Floor ${EnglishOrdinal[Number(floor?.number) - 1]}`
                  : t("qrCodeCard.floorDeleted")
              } - ${
                table?.number
                  ? i18n.language === "ar"
                    ? `الطاولة رقم ${table?.number}`
                    : `Table Number ${table?.number}`
                  : t("qrCodeCard.tableDeleted")
              }`
            : link}
        </p>
      </td>

      {/* type */}
      <td className="px-6 py-4 text-sm text-gray-600">
        <p>
          {!link && (!table || !floor || !branch)
            ? t("qrCodeCard.notAssigned")
            : type === "table"
              ? t("qrCodeCard.table")
              : type === "link"
                ? t("qrCodeCard.link")
                : t("qrCodeCard.notAssigned")}
        </p>
      </td>

      {/* status */}
      <td className="px-6 py-4 text-center text-sm text-gray-600">
        {isAvailableNow ? (
          <span className="flex w-fit items-center justify-start rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-500 ">
            {t("qrCodeCard.available")}
          </span>
        ) : (
          <span className="flex w-fit items-center justify-start rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-500 ">
            {t("qrCodeCard.notAvailable")}
          </span>
        )}
      </td>

      {/* actions */}
      <td className="px-6 py-4 text-sm text-gray-600">
        <button
          onClick={toggleDropdown}
          ref={dropDownButtonRef}
          type="button"
          className={`relative col-start-2 col-end-3 row-start-1 row-end-2 flex w-[30px] items-center justify-center gap-3 px-1 py-2 text-black ${isDropdownOpen ? "rounded-md bg-sky-50" : ""}`}
        >
          <HiDotsVertical />
          {isDropdownOpen && (
            <div
              ref={dropdownRef}
              className="absolute start-[37px] top-[-65%] z-[9999] flex h-[65px] w-[35px] cursor-default flex-col rounded-md border border-gray-200 bg-gray-100 shadow-md"
            >
              <span className="flex h-[32.5px] items-center rounded-t-md text-sm text-black hover:bg-sky-100">
                <QrCodeEditModal
                  _id={_id}
                  link={link}
                  type={type}
                  setNeedRefetch={setNeedRefetch}
                  floor={floor}
                  table={table}
                />
              </span>
              <span className="flex h-[32.5px] items-center rounded-b-md text-sm text-black hover:bg-sky-100">
                <DeleteQrCodeModal setNeedRefetch={setNeedRefetch} _id={_id} />
              </span>
            </div>
          )}
        </button>
      </td>
    </tr>
  );
};

export default QrCodeCard;
